import { useState, useEffect, useRef } from "react";
import { X, ChevronLeft, ChevronRight } from "react-feather";
import LoadingSpinner from "../../components/LoadingSpinner";

const PAGE_SIZE = 10;

const fmt = (val, decimals = 2) => {
  const n = parseFloat(val);
  if (isNaN(n)) return "—";
  return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: decimals });
};

const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  if (totalPages <= 1) return null;
  const pages = [];
  const delta = 2;
  const left = currentPage - delta;
  const right = currentPage + delta;
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || (i >= left && i <= right)) pages.push(i);
  }
  const withEllipsis = [];
  let prev = null;
  for (const page of pages) {
    if (prev && page - prev > 1) withEllipsis.push("...");
    withEllipsis.push(page);
    prev = page;
  }
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "4px", padding: "12px 0 4px", borderTop: "1px solid #f0f0f0" }}>
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1} style={{ border: "1px solid #e0e0e0", borderRadius: "6px", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: currentPage === 1 ? "not-allowed" : "pointer", background: "#fff", opacity: currentPage === 1 ? 0.4 : 1 }} onMouseEnter={(e) => { if (currentPage !== 1) e.currentTarget.style.background = "#f5f5f5"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
        <ChevronLeft size={14} />
      </button>
      {withEllipsis.map((p, i) =>
        p === "..." ? (
          <span key={`e${i}`} style={{ padding: "0 4px", color: "#aaa", fontSize: "13px" }}>…</span>
        ) : (
          <button key={p} onClick={() => onPageChange(p)} style={{ border: `1px solid ${currentPage === p ? "#0F6E56" : "#e0e0e0"}`, borderRadius: "6px", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: "12px", fontWeight: currentPage === p ? 700 : 400, background: currentPage === p ? "#0F6E56" : "#fff", color: currentPage === p ? "#fff" : "#444" }} onMouseEnter={(e) => { if (currentPage !== p) e.currentTarget.style.background = "#f0fff8"; }} onMouseLeave={(e) => { if (currentPage !== p) e.currentTarget.style.background = "#fff"; }}>
            {p}
          </button>
        )
      )}
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages} style={{ border: "1px solid #e0e0e0", borderRadius: "6px", width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: currentPage === totalPages ? "not-allowed" : "pointer", background: "#fff", opacity: currentPage === totalPages ? 0.4 : 1 }} onMouseEnter={(e) => { if (currentPage !== totalPages) e.currentTarget.style.background = "#f5f5f5"; }} onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; }}>
        <ChevronRight size={14} />
      </button>
    </div>
  );
};

const RevenueModal = ({ isOpen, onClose, data, loading, error }) => {
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const overlayRef = useRef(null);

  useEffect(() => { setSearch(""); setCurrentPage(1); }, [isOpen]);
  useEffect(() => { setCurrentPage(1); }, [search]);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    if (isOpen) document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const jobs = data?.job_wise ?? [];
  const totalCost = data != null ? (data.total_cost ?? 0) : 0;

  const filtered = jobs.filter((j) =>
    j.job_code.toLowerCase().includes(search.toLowerCase())
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);
  const startIndex = (safePage - 1) * PAGE_SIZE;
  const filteredTotalCost = filtered.reduce((s, j) => s + parseFloat(j.cost || 0), 0);

  const thStyle = {
    padding: "10px 14px", fontWeight: 600, color: "#555",
    borderBottom: "1px solid #eee", background: "#f8f9fa",
    position: "sticky", top: 0, zIndex: 1,
  };

  return (
    <div
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{ position: "fixed", inset: 0, zIndex: 9999, background: "rgba(0,0,0,0.45)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", animation: "revFadeIn 0.15s ease" }}
    >
      <div style={{ background: "#fff", borderRadius: "14px", width: "min(680px, 95vw)", maxHeight: "85vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.25)", animation: "revSlideUp 0.2s ease", overflow: "hidden" }}>

        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#0F6E56", flexShrink: 0 }} />
            <span style={{ fontWeight: 700, fontSize: "16px", color: "#1a1a2e" }}>Job Wise Revenue</span>
            <span style={{ background: "#0F6E5622", color: "#0F6E56", borderRadius: "20px", padding: "2px 10px", fontSize: "12px", fontWeight: 600 }}>
              Total: {fmt(totalCost, 2)}
            </span>
          </div>
          <button onClick={onClose} style={{ background: "#f5f5f5", border: "none", borderRadius: "8px", width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", transition: "background 0.15s", flexShrink: 0 }} onMouseEnter={(e) => (e.currentTarget.style.background = "#e8e8e8")} onMouseLeave={(e) => (e.currentTarget.style.background = "#f5f5f5")}>
            <X size={15} />
          </button>
        </div>

        {loading ? (
          <div style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div style={{ height: "300px", display: "flex", alignItems: "center", justifyContent: "center", color: "#dc2626", fontSize: "13px" }}>
            {error}
          </div>
        ) : (
          <>
            <div style={{ padding: "12px 22px 8px", flexShrink: 0 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px" }}>
                <input
                  type="text"
                  placeholder="Search job code..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ flex: 1, padding: "8px 12px", borderRadius: "8px", border: "1px solid #e0e0e0", fontSize: "13px", outline: "none", transition: "border-color 0.15s", boxSizing: "border-box" }}
                  onFocus={(e) => (e.target.style.borderColor = "#0F6E56")}
                  onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                  autoFocus
                />
                <span style={{ fontSize: "12px", color: "#888", whiteSpace: "nowrap", flexShrink: 0 }}>
                  {filtered.length} record{filtered.length !== 1 ? "s" : ""}
                </span>
              </div>
            </div>

            <div style={{ overflowY: "auto", padding: "0 22px", flex: 1 }}>
              {filtered.length === 0 ? (
                <div style={{ textAlign: "center", color: "#999", padding: "32px", fontSize: "14px" }}>No jobs match your search.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: "left", width: "44px" }}>#</th>
                      <th style={{ ...thStyle, textAlign: "left" }}>Job Code</th>
                      <th style={{ ...thStyle, textAlign: "right" }}>Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((job, i) => {
                      const rowIndex = startIndex + i;
                      const isEven = rowIndex % 2 === 0;
                      return (
                        <tr key={job.job_code} style={{ background: isEven ? "#fff" : "#fafafa", transition: "background 0.1s" }} onMouseEnter={(e) => (e.currentTarget.style.background = "#f0fff8")} onMouseLeave={(e) => (e.currentTarget.style.background = isEven ? "#fff" : "#fafafa")}>
                          <td style={{ padding: "10px 14px", color: "#bbb", fontSize: "11px", borderBottom: "1px solid #f0f0f0" }}>{rowIndex + 1}</td>
                          <td style={{ padding: "10px 14px", fontFamily: "monospace", fontWeight: 600, color: "#0F6E56", borderBottom: "1px solid #f0f0f0" }}>{job.job_code}</td>
                          <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 600, color: "#1a1a2e", borderBottom: "1px solid #f0f0f0" }}>{fmt(job.cost, 2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: "#f0fff8", fontWeight: 700 }}>
                      <td colSpan={2} style={{ padding: "10px 14px", fontSize: "12px", color: "#555", borderTop: "2px solid #c6e8d8" }}>
                        Total ({filtered.length} job{filtered.length !== 1 ? "s" : ""})
                      </td>
                      <td style={{ padding: "10px 14px", textAlign: "right", color: "#0F6E56", borderTop: "2px solid #c6e8d8" }}>
                        {fmt(filteredTotalCost, 2)}
                      </td>
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>

            <div style={{ padding: "0 22px 12px", flexShrink: 0 }}>
              <Pagination currentPage={safePage} totalPages={totalPages} onPageChange={setCurrentPage} />
              {totalPages > 1 && (
                <div style={{ textAlign: "center", fontSize: "11px", color: "#aaa", marginTop: "6px" }}>
                  Showing {startIndex + 1}–{Math.min(startIndex + PAGE_SIZE, filtered.length)} of {filtered.length}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <style>{`
        @keyframes revFadeIn { from { opacity:0 } to { opacity:1 } }
        @keyframes revSlideUp { from { transform:translateY(20px); opacity:0 } to { transform:translateY(0); opacity:1 } }
      `}</style>
    </div>
  );
};

export default RevenueModal;